import { createContext, useContext, useEffect, useCallback, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '../store/hooks'
import { updateLastWorkspaceSaga } from '../store/auth/authSlice'
import {
    startListening,
} from '../store/workspace/workspaceSlice'
import {
    selectWorkspace,
    selectStatuses,
    selectLabels,
    selectMembers,
    selectWorkspaceLoading,
    selectWorkspaceError
} from '../store/workspace/workspaceSelectors'

// Types
export interface Workspace {
    id: string
    name: string
    workspaceUrlId: string
    workspaceTaskPrefixId: string
    avatar: string | null
    companySize: string | null
    timezone: string | null
    createdBy: string
    taskCounter: number
    planId: 'free' | 'pro' | null
    // Subscription lifecycle (SCD-987) — the field gating reads. `planId` is kept for
    // back-compat with docs not yet migrated (008).
    planStatus?: 'free' | 'active' | 'canceling' | 'canceled' | null
    activeMembers?: number
    paidThroughDate?: any
    figmaAccessToken?: string
    storageUsedBytes?: number
    // Slack — display-only fields (the bot token lives server-side in slack_installs).
    slackConnected?: boolean
    slackTeamName?: string
    slackTeamId?: string
    slackNotifyChannelName?: string
}

export interface Status {
    id: string
    name: string
    color: string
    systemType: string | null
    order: number
}

export interface Label {
    id: string
    name: string
    // Absent on labels created before colors existed — read through `labelColor()`.
    color?: string | null
    order: number
}

export interface WorkspaceMember {
    id: string
    workspaceId: string
    userId: string
    role: 'owner' | 'member' | 'guest'
    status: 'active' | 'suspended' | 'pending'
    createdAt: Date | null
    // User profile data
    email: string
    fullName: string
    username: string
    color: string
    avatarImage: string | null
}

export interface WorkspaceContextValue {
    workspace: Workspace | null
    statuses: Status[]
    labels: Label[]
    members: WorkspaceMember[]
    getMember: (userId: string) => WorkspaceMember | undefined
    getStatusColor: (statusId: string) => string | undefined
    loading: boolean
    error: string | null
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null)

export const useWorkspace = () => {
    const context = useContext(WorkspaceContext)
    if (!context) {
        throw new Error('useWorkspace must be used within WorkspaceProvider')
    }
    return context
}

interface WorkspaceProviderProps {
    children: ReactNode
}

export const WorkspaceProvider = ({ children }: WorkspaceProviderProps) => {
    const dispatch = useAppDispatch()
    const location = useLocation()
    const workspaceUrlId = location.pathname.split('/')[1] || undefined
    const auth = useAppSelector(state => state.auth)

    useEffect(() => {
        if (workspaceUrlId) {
            dispatch(startListening(workspaceUrlId))
        }
    }, [workspaceUrlId, auth?.id, dispatch])

    const workspace = useAppSelector(selectWorkspace)

    // Remember the last opened workspace (on the user doc, so it follows them
    // across devices) — the next visit without a workspace in the URL returns here.
    useEffect(() => {
        if (workspace?.workspaceUrlId) dispatch(updateLastWorkspaceSaga(workspace.workspaceUrlId))
    }, [workspace?.workspaceUrlId, dispatch])

    const statuses = useAppSelector(selectStatuses)
    const labels = useAppSelector(selectLabels)
    const members = useAppSelector(selectMembers)
    const loading = useAppSelector(selectWorkspaceLoading)
    const error = useAppSelector(selectWorkspaceError)

    const getMember = useCallback((userId: string): WorkspaceMember | undefined => {
        if (userId?.startsWith('deleted_')) {
            return {
                id: userId,
                workspaceId: '',
                userId,
                role: 'member',
                status: 'suspended',
                createdAt: null,
                email: '',
                fullName: 'Deleted Member',
                username: 'deleted member',
                color: 'var(--light-grey)',
                avatarImage: null,
            }
        }
        return members.find((m: WorkspaceMember) => m.userId === userId || m.id === userId)
    }, [members])

    const getStatusColor = useCallback((statusId: string): string | undefined => {
        return statuses.find((s: Status) => s.id === statusId)?.color
    }, [statuses])

    const value: WorkspaceContextValue = {
        workspace,
        statuses,
        labels,
        members,
        getMember,
        getStatusColor,
        loading,
        error
    }

    return (
        <WorkspaceContext.Provider value={value}>
            {children}
        </WorkspaceContext.Provider>
    )
}

// Supplies a pre-built context value (no Redux/listeners) — used by the public
// read-only project view (SCD-541) so in-app components like Section render 1:1.
export const StaticWorkspaceProvider = ({ value, children }: { value: WorkspaceContextValue; children: ReactNode }) => (
    <WorkspaceContext.Provider value={value}>
        {children}
    </WorkspaceContext.Provider>
)
